import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Title } from "../components/common/Title";
import GapsChart from "../components/doubles/GapsChart";
import RoadChart from "../components/doubles/RoadChart";
import SeasonChart from "../components/doubles/SeasonChart";
import { useApiResource } from "../hooks/useApiResource";
import type { DoubleStats } from "../types/doubleStats";
import "./Doubles.css";

const API_HOST = process.env.REACT_APP_API;

const sectionTitle = {
  color: "rgb(70, 130, 180)",
  marginTop: "32px",
  marginBottom: "4px"
};

const formatDays = (days: number | null) =>
  days === null ? "-" : `${days.toFixed(2)} days`;

const formatMonthYear = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric"
      })
    : "-";

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      })
    : "-";

// Missing dates sort last
const dateValue = (iso: string | null) =>
  iso ? new Date(iso).getTime() : Number.MAX_SAFE_INTEGER;

const formatBillions = (deaths: number) =>
  `${Number((deaths / 1e9).toFixed(3))}B`;

const StatCard = ({
  label,
  value,
  sub
}: {
  label: string;
  value: string;
  sub?: string;
}) => (
  <div className="doubles-stat-card">
    <div className="doubles-stat-label">{label}</div>
    <div className="doubles-stat-value">{value}</div>
    {sub && <div className="doubles-stat-sub">{sub}</div>}
  </div>
);

export const Doubles = () => {
  const { data, loading, error, reload } = useApiResource<DoubleStats>(
    `${API_HOST}public/double-stats`,
    "doubles"
  );

  if (loading && !data) {
    return (
      <Container>
        <Title text="Doubles" />
        <Spinner animation="border" variant="light" />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container>
        <Title text="Doubles" />
        <div className="text-light mb-3">
          {error?.message || "Couldn't load double stats."}
        </div>
        <Button variant="outline-light" size="sm" onClick={reload}>
          Try again
        </Button>
      </Container>
    );
  }

  const { stats, projection, seasonality } = data;
  const target = formatBillions(projection.target);
  const seasonal = projection.usesSeasonality;

  const year = new Date(data.latestSnapshotAt).getUTCFullYear();
  const countIn = (y: number) =>
    data.crossings.filter((c) => new Date(c.crossedAt).getUTCFullYear() === y)
      .length;
  // History starts mid-2024, so only compare against a year it fully covers
  const hasFullPrevYear =
    data.crossings.length > 0 &&
    new Date(data.crossings[0].crossedAt).getTime() <
      Date.UTC(year - 1, 0, 1);

  const range = [
    {
      label: `Pace +${projection.bandPct}%`,
      at: projection.fastAt
    },
    {
      label: seasonal ? "Seasonal projection" : "Projection",
      at: projection.expectedAt,
      main: true
    },
    {
      label: `Pace -${projection.bandPct}%`,
      at: projection.slowAt
    },
    { label: "Flat average pace", at: projection.flatAt }
  ].sort((a, b) => dateValue(a.at) - dateValue(b.at));

  return (
    <Container>
      <Title text="Doubles" />
      <div className="text-light mb-4">
        A double starts every 1,000,000 mob deaths.
      </div>

      <Row className="g-3 mb-2">
        <Col xs={6} lg>
          <StatCard
            label="Between doubles (all time)"
            value={formatDays(stats.avgDaysAll)}
            sub={`${stats.intervals} intervals`}
          />
        </Col>
        <Col xs={6} lg>
          <StatCard
            label={`Last ${stats.recentWindowDays} days`}
            value={formatDays(stats.avgDaysRecent)}
            sub={
              stats.minDaysRecent !== null && stats.maxDaysRecent !== null
                ? `${stats.minDaysRecent.toFixed(1)} to ${stats.maxDaysRecent.toFixed(1)} days`
                : undefined
            }
          />
        </Col>
        <Col xs={6} lg>
          <StatCard
            label="Last double"
            value={
              stats.lastDoubleMilestone
                ? formatBillions(stats.lastDoubleMilestone)
                : "-"
            }
            sub={formatDate(stats.lastDoubleAt)}
          />
        </Col>
        <Col xs={6} lg>
          <StatCard
            label={`Doubles in ${year}`}
            value={String(countIn(year))}
            sub={
              hasFullPrevYear
                ? `${countIn(year - 1)} in all of ${year - 1}`
                : "so far this year"
            }
          />
        </Col>
      </Row>
      <div className="doubles-footnote mb-2">
        {data.currentDeaths.toLocaleString()} mob deaths as of{" "}
        {new Date(data.latestSnapshotAt).toLocaleString(undefined, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit"
        })}
      </div>

      <h4 style={sectionTitle}>Days between doubles</h4>
      <div className="doubles-footnote mb-2">
        Each dot is the days since the previous double; the line averages the
        last 12, so lower means doubles are coming faster. Shaded stretches are
        December to April, when kills run highest.
      </div>
      <GapsChart gaps={data.gaps} avgDaysAll={stats.avgDaysAll} />

      <h4 style={sectionTitle}>Road to {target}</h4>
      <div className="doubles-footnote mb-2">
        Solid is deaths so far, dashed is the projection
        {seasonal
          ? ", which follows the yearly pattern of busier and quieter months. "
          : ", which assumes the recent pace holds. "}
        The shaded band is the arrival date if kills run {projection.bandPct}%
        faster or slower.
      </div>
      <RoadChart
        crossings={data.crossings}
        currentDeaths={data.currentDeaths}
        latestSnapshotAt={data.latestSnapshotAt}
        projection={projection}
      />
      <Row className="g-3 mt-1">
        {range.map((r) => (
          <Col xs={6} lg={3} key={r.label}>
            <div
              className={`doubles-range ${r.main ? "doubles-range-main" : ""}`}
            >
              <div className="doubles-range-label">{r.label}</div>
              <div className="doubles-range-value">{formatMonthYear(r.at)}</div>
            </div>
          </Col>
        ))}
      </Row>

      {seasonality && (
        <>
          <h4 style={sectionTitle}>Busy and quiet months</h4>
          <div className="doubles-footnote mb-2">
            Each bar is how much faster or slower kills run that month than the
            yearly average, with overall growth (about{" "}
            {seasonality.trendPctPerMonth.toFixed(1)}% a month) taken out.
            December to April runs about{" "}
            {Math.round(seasonality.winterVsSummerPct)}% above May to October.
          </div>
          <SeasonChart factors={seasonality.factors} />
        </>
      )}

      <div className="doubles-footnote mt-4 mb-4">
        The projection holds today's kill level steady; if kills keep growing,
        the date comes sooner.
      </div>
    </Container>
  );
};

export default Doubles;
